import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, ChevronRight, CreditCard, DollarSign, TrendingUp, Clock, Search, ArrowRight, Star } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-emerald-500", "bg-emerald-400", "bg-teal-500", "bg-cyan-500", "bg-emerald-600", "bg-green-500", "bg-teal-400", "bg-sky-500"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-9 h-9 text-xs rounded-lg" : size === "lg" ? "w-14 h-14 text-lg rounded-xl" : "w-10 h-10 text-sm rounded-lg";
  return <div className={`${s} ${colors[i]} flex items-center justify-center flex-shrink-0`}><span className="text-white font-semibold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

export const VaultView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite, searchQuery, setSearchQuery } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);
  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { id: "all", label: "All Assets", count: offers.length },
    { id: "expiring", label: "Expiring", count: expiringSoon.length },
    { id: "top", label: "Top Value", count: bestValue.length },
  ];
  const displayOffers = activeTab === "expiring" ? expiringSoon : activeTab === "top" ? bestValue : offers;

  return (
    <div className="min-h-screen bg-white pb-28" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Portfolio card */}
      <div className="px-5 pt-8 pb-4">
        <p className="text-slate-400 text-xs font-medium tracking-wide">PORTFOLIO</p>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden"
        >
          {/* Card chip */}
          <div className="flex items-center justify-between mb-8">
            <div className="w-10 h-7 rounded bg-gradient-to-br from-amber-300 to-amber-500 opacity-80" />
            <CreditCard className="w-5 h-5 text-slate-500" />
          </div>
          <p className="text-slate-400 text-[10px] tracking-widest uppercase">Total Asset Value</p>
          <p className="text-4xl font-bold mt-1 tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>
            ${totalValue.toFixed(2)}
          </p>
          <div className="flex items-center gap-6 mt-5 pt-4 border-t border-white/10">
            <div>
              <p className="text-slate-500 text-[10px]">Holdings</p>
              <p className="text-white font-semibold">{offers.length}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">At Risk</p>
              <p className="text-red-400 font-semibold">{expiringSoon.length}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Matures In</p>
              <p className="text-emerald-400 font-semibold">{stats.days_until_birthday}d</p>
            </div>
          </div>
          {/* Card number aesthetic */}
          <p className="text-slate-600 text-[10px] tracking-[0.3em] mt-4">**** **** **** {user?.first_name?.toUpperCase()?.slice(0, 4) || "USER"}</p>
        </motion.div>
      </div>

      {/* Search */}
      <div className="px-5 mb-4">
        <div className="flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-lg">
          <Search className="w-4 h-4 text-slate-300" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search holdings..."
            className="flex-1 text-sm outline-none text-slate-700 placeholder-slate-300"
            data-testid="vault-search"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 mb-4 flex gap-1 border-b border-slate-100">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2.5 text-xs font-medium transition-colors relative ${
              activeTab === tab.id ? "text-slate-900" : "text-slate-400"
            }`}
            data-testid={`vault-tab-${tab.id}`}
          >
            {tab.label} <span className="text-slate-300 ml-1">{tab.count}</span>
            {activeTab === tab.id && (
              <motion.div layoutId="vault-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
            )}
          </button>
        ))}
      </div>

      {/* Passbook-style list */}
      <div className="px-5">
        {displayOffers.slice(0, 20).map((offer, i) => (
          <motion.div
            key={offer.business_id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            onClick={() => onOfferClick(offer)}
            className="flex items-center gap-3 py-3.5 border-b border-slate-50 cursor-pointer group"
            data-testid={`vault-offer-${offer.business_id}`}
          >
            <MiniLogo name={offer.name} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{offer.name}</p>
              {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
              <p className="text-xs text-slate-400">{offer.category}</p>
            </div>
            <div className="text-right flex items-center gap-3">
              <div>
                <p className="text-sm font-semibold text-emerald-600 tabular-nums">${offer.offer_value_est?.toFixed(2)}</p>
                {offer.ease_stars >= 4 && <p className="text-[10px] text-emerald-400">Easy claim</p>}
              </div>
              <button onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }} className="p-1">
                <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-200"}`} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
